import DashboardLayout from "../../lib/components/layouts/dashboard";
import { PAGE_ROUTES } from "lib/constants/page-routes";
import { Col, Row } from "react-bootstrap";
import { FC, useContext, useEffect, useState } from "react";

import usePatientBookings from "lib/hooks/usePatientBookings";

import SimpleCard from "lib/components/common/simple-card";

import SimpleButton from "lib/components/common/simple-button";
import SimpleList from "lib/components/common/simple-list";
import { AppContext } from "lib/contexts/app-context";
import usePractitioner from "lib/hooks/usePractitioner";
import BookingService from "lib/services/appointment-booking-service";
import BookingDetailsTile from "lib/components/appointments/booking-details-tile";
import LoadingBox from "lib/components/common/loading-box";
import {
  areDatesEqual,
  getDatePart,
  getDateWithTimeFromTimePart,
  getLocalISO,
} from "lib/utils/date-utils";
import SimpleTimePicker from "lib/components/common/calendar/simple-timepicker";
import { toast } from "sonner";
import { DateTime } from "luxon";
import { EMPTY_GUID } from "lib/constants";

import { useNavigate } from "react-router-dom";

const breadcrumbItems: BreadcrumbItem[] = [
  {
    label: "Calender",
    path: PAGE_ROUTES.PRACTITIONER.AppointmentList,
  },
];

interface AppointPageProps {
  practitionerId?: string;
  definedDate?: Date;
}

const AppointmentPage: FC<AppointPageProps> = ({
  practitionerId,
  definedDate,
}) => {
  const navigateTo = useNavigate();
  const { appState } = useContext(AppContext);
  const userId = practitionerId || appState?.user_id;

  const {
    appointments,
    getAppointments,
    getAppointmentDetails,
    bookBusyAppointment,
    cancelAppointment,
    updateAppointmentStatus,
    loading: appointmentsLoading,
    error: appointmentsError,
  } = usePatientBookings();
  const {
    getPractitionerSubscriptions,
    getPractitionerTherapies,
    loading: subscriptionsLoading,
    error: subscriptionsError,
  } = usePractitioner();

  const [selectedDate, setSelectedDate] = useState<Date | null>(definedDate);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number | null>(
    null
  );

  const [bookingSlots, setBookingSlots] = useState<AppointmentBooking[]>([]);
  const [selectedBookingDetails, setSelectedBookingDetails] =
    useState<AppointmentBookingDetails | null>(null);
  const [subscriptionDates, setSubscriptionDates] = useState<string[]>([]);
  const [selectedSubscriptionDateIndex, setSelectedSubscriptionDateIndex] =
    useState<number | null>(null);
  const [selectedWorkingTime, setSelectedWorkingTime] =
    useState<WorkingTime | null>(null);
  const [subscriptions, setSubscriptions] = useState<
    PractitionerSubscriptionDetails[]
  >([]);
  const [therapies, setTherapies] = useState<PractitionerTherapy[]>([]);

  const refreshData = () => {
    setSelectedDate(null);
    setSelectedBookingDetails(null);
    setSelectedSlotIndex(null);
    setSelectedSubscriptionDateIndex(null);
    setBookingSlots([]);
    setSelectedWorkingTime(null);

    getPractitionerSubscriptions(userId, (value) => {
      setSubscriptions(value);

      if (value?.length > 0) {
        let allSubscribedDates = [];
        (value ?? []).map((subscription) =>
          allSubscribedDates.push(...subscription.dates)
        );

        const allDates = (allSubscribedDates ?? [])
          .map((date) => new Date(date)) // convert to Date objects
          .filter((d) => !isNaN(d.getTime())) // ensure valid dates
          .sort((a, b) => a.getTime() - b.getTime()) // ascending order
          .map((d) => d.toDateString()); // format for display
        setSubscriptionDates(allDates);

        getAppointments(userId);
        getPractitionerTherapies(userId, setTherapies);
      }
    });
    };

     useEffect(() => refreshData(), []);

  /** Filter subscriptions for this practitioner */
  useEffect(() => {
   if (selectedDate) {
      const filteredAppointments = appointments.filter((appointment) =>
        areDatesEqual(new Date(appointment.fromTime), selectedDate)
      );

      setBookingSlots(
        BookingService.formatBookingDetails(filteredAppointments)
      );
      setSelectedSlotIndex(null);
      setSelectedBookingDetails(null);
    }
    }, [selectedDate]);

  const handleBookingClick = (index: number) => {
    setSelectedSlotIndex(index);
    if (bookingSlots?.[index]?.appointmentId) {
      getAppointmentDetails(
        bookingSlots?.[index]?.appointmentId,
        setSelectedBookingDetails
      );
      } else {
      setSelectedBookingDetails(null);
    }
  };

  const handleMarkAsBusyClick = () => {
    if (!selectedWorkingTime) {
      toast.error("Please select the time to mark as busy.");
      return;
    }

    const isOverlapping = BookingService.isBookingOverlapping(
      bookingSlots,
      selectedWorkingTime
    );

    if (isOverlapping) {
      toast.error("The selected time overlaps with the bookings.");
      return;
    }

    const fromDate = DateTime.fromISO(
      getLocalISO(
        getDateWithTimeFromTimePart(selectedDate, selectedWorkingTime.fromTime)
      )
    );

    const toDate = DateTime.fromISO(
      getLocalISO(
        getDateWithTimeFromTimePart(selectedDate, selectedWorkingTime.toTime)
      )
    );

    const duration = toDate.diff(fromDate, "minutes").minutes;

    const selectedSubscription = subscriptions?.find((subscription) => {
      return (subscription.dates ?? []).some((date) => {
        const subscriptionDate = new Date(date);

        return areDatesEqual(
          getDatePart(subscriptionDate),
          getDatePart(selectedDate)
        );
      });
    });

    const therapyId = therapies?.[0]?.therapyId || EMPTY_GUID;

    const busyAppointment: AppointmentBookingDetails = {
      fromTime: fromDate.toISO(),
      duration,
      practitionerId: userId,
      patientId: userId,
      clinicId: selectedSubscription?.clinicId,
      therapyId,
      totalCost: 0,
      status: "busy",
    };

  /** Handle calendar slot click */
  const handleSelectDate = (date: Date) => {
    setSelectedDate(date);
  };

  bookBusyAppointment(busyAppointment, (result) => {
      toast.success("Appointment booked successfully.");
      refreshData();
    });
  };

  const handleCancelAppointment = (bookingId: string) => {
    cancelAppointment(bookingId, () => {
      toast.success("Appointment cancelled successfully.");
      refreshData();
    });
  };

  const handleNewAppointmentClick = () => {
    navigateTo(PAGE_ROUTES.PRACTITIONER.BookAppointment);
  };

  const child = (
    <SimpleCard
      title="Manage Appointments"
      action={
        !definedDate && (
          <SimpleButton onClick={handleNewAppointmentClick}>
            New Appointment
          </SimpleButton>
        )
      }
      error={appointmentsError || subscriptionsError}
    >
      <Row>
        <Col sm="4">
          <SimpleList
            className="w-100 auto-scroll-300"
            items={subscriptionDates}
            selectedItemIndex={selectedSubscriptionDateIndex}
            onItemClick={(index) => {
              setSelectedSubscriptionDateIndex(index);
              setSelectedDate(new Date(subscriptionDates[index]));
            }}
          />
          <SimpleTimePicker
            onChange={setSelectedWorkingTime}
            value={selectedWorkingTime}
            disabled={!selectedDate}
          />
          <SimpleButton isBlockButton onClick={handleMarkAsBusyClick}>
                      Mark as busy
                    </SimpleButton>
                  </Col>
                  <Col sm="4">
                    <h5>Booked Appointments</h5>
                    {bookingSlots?.length > 0 ? (
                      <SimpleList
                        items={BookingService.formatBookingSlots(bookingSlots)}
                        selectedItemIndex={selectedSlotIndex}
                        onItemClick={handleBookingClick}
                        className="w-100 auto-scroll-300"
                      />
                    ) : (
                      <div className="">
                        <p>No appointments booked for the selected date.</p>
                      </div>
                    )}
                  </Col>
                  <Col sm="4">
                    {appointmentsLoading || subscriptionsLoading ? (
                      <LoadingBox />
                    ) : (
                      <>
                        {selectedBookingDetails && (
                          <BookingDetailsTile
                            bookingDetails={selectedBookingDetails}
                            onCancelAppointment={handleCancelAppointment}
                            onCompleteAppointment={(bookingId, status) => {
                              if (!bookingId) {
                                toast.error("Appointment ID not found. Please try again.");
                                return;
                              }
                              if (!status) {
                                toast.error("No status provided for update.");
                                return;
                              }
                              updateAppointmentStatus(bookingId, status, (result) => {
                                if (result?.id) {
                                  toast.success(
                                    status === "scheduled"
                                      ? "Appointment marked as paid."
                                      : "Appointment marked as completed."
                                  );
                                  refreshData();
                                } else {
                                  toast.error("Failed to update appointment status. Please try again.");
                                }
                              });
                            }}
                            onEditAppointment={(bookingId) => {
                              navigateTo(PAGE_ROUTES.ADMIN.EditAppointment, {
                                state: { bookingId }
                              });
                            }}
                          />
                        )}
                      </>
                    )}
                  </Col>
                </Row>
              </SimpleCard>
            );

            if (practitionerId) return child;

            return (
              <DashboardLayout
                breadcrumbItems={breadcrumbItems}
                //isUnavailable={practitionerSubscriptionState.status === "success"}
              >
                {child}
    </DashboardLayout>
  );
};

export default AppointmentPage;
